import { ROUTES } from 'app/utils/constants/paths';
import { frontoffice } from 'core';
import { ChatbotWidget } from 'core/chatbot';
import { createChatService } from 'core/chatbot/services/chatService';
import { useUser } from 'core/lite/hooks/useUser';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useIsAiEnabled from './useIsAiEnabled';

/**
 * Tangerine-specific implementation of the BaseChatbotWidget
 * This component applies Tangerine theming and provides contextual awareness
 * for the specific application.
 */
function TangerineAIChatbot({ children }) {
  // Get current location for route-based visibility
  const location = useLocation();

  // Get relevant state from the application
  const currentUser = useSelector((state) => state.auth?.user || {});
  const currentLesson = useSelector((state) => state.lessons?.currentLesson || {});
  const currentCourse = useSelector((state) => frontoffice.courses.selectors.getCurrentCourse(state));
  const currentUnit = useSelector((state) => state.courses?.currentUnit || {});

  const excludeRoutes = [
    '/login',
    '/logout',
    '/signup',
    '/forgot-password',
    '/reset-password',
    '/error',
    '/credits',
    '/terms-of-use',
    '/privacy-policy',
    '/teacher-tools/', // Hide floating chatbot on teacher tools routes
  ];
  // hide floating chatbot but needs ChatbotProvider to be present in the route to work
  const excludeRoutesWithFloatingButton = [
    /^\/activities\/sessions\/[^/]+\/summary$/,
    /^\/results-assesment-individual\//, // Hide on assessment result pages (summary and question) but keep provider
  ];

  const emptyStateTitle = '🍊 Tangerine AI';
  const emptyStateText = '¿Cómo puedo ayudarte con tu aprendizaje hoy?';

  const { isStudent } = useUser();
  const isAiEnabled = useIsAiEnabled();

  // Function to dynamically get the auth token when needed by the chat service
  const getAuthToken = useCallback(() => {
    try {
      // Get from localStorage if available
      const persistedAuth = localStorage.getItem('persist:i2c_auth');
      if (persistedAuth) {
        const authData = JSON.parse(persistedAuth);
        if (authData && authData.authToken) {
          // The token might be stored with quotes
          const token = JSON.parse(authData.authToken);
          return token;
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }, [currentUser?.guid]);

  /**
   * Get context data for the current application state
   * This function gathers relevant information to provide context to the chatbot
   */
  const getChatContextData = useCallback(() => {
    // Base context that's always available
    const context = {
      userGuid: currentUser?.guid,
      currentPage: window.location.pathname,
      timestamp: new Date().toISOString(),
    };

    // Add lesson-specific context if available
    if (currentLesson?.guid) {
      context.lessonGuid = currentLesson.guid;
      context.lessonTitle = currentLesson.title;
    }

    // Add course-specific context if available
    if (currentCourse?.guid) {
      context.courseGuid = currentCourse.guid;
      context.courseName = currentCourse.name;
    }

    // Add unit-specific context if available
    if (currentUnit?.guid) {
      context.unitGuid = currentUnit.guid;
      context.unitName = currentUnit.name;
    }

    // Return the consolidated context
    return context;
  }, [currentUser, currentLesson, currentCourse, currentUnit]);

  /**
   * Determine chatbot mode based on current URL
   */
  const determineChatbotMode = useCallback(() => {
    const currentPath = window.location.pathname;
    // Reconoce tanto la ruta singular como la de sesión
    if (currentPath === ROUTES.CHATBOT_PRACTICE || currentPath.startsWith('/activities/sessions/')) {
      return 'practice';
    }
    return 'study';
  }, []);

  /**
   * Get practice page URL patterns for the chatbot to recognize
   */
  const practicePageUrls = useMemo(() => {
    return [ROUTES.CHATBOT_PRACTICE];
  }, []);

  // Check if chatbot should be visible on current route
  const shouldRenderOnCurrentRoute = useMemo(() => {
    const currentPath = location.pathname;

    // Check if current path matches any of the excluded routes
    const isExcludedPath = excludeRoutes.some((route) => currentPath === route || currentPath.startsWith(route));

    return !isExcludedPath;
  }, [location.pathname, excludeRoutes]);

  const shouldHideFloatingButton = useMemo(() => {
    return excludeRoutesWithFloatingButton.some((route) => route.test(location.pathname));
  }, [location.pathname, excludeRoutesWithFloatingButton]);

  // Create Tangerine-specific chat service with memoization and auth token
  const tangerineChatService = useMemo(
    () =>
      createChatService({
        // Custom error handling
        onError: (error) => {
          console.error('Tangerine Chat Service Error:', error);
          // Add any application-specific error handling here
        },
        // Pass token access method
        getAuthToken: getAuthToken,
      }),
    [getAuthToken]
  );

  // Callback handlers for analytics or side effects
  const handleOpen = () => {
    // Custom open logic (e.g., analytics tracking)
    const currentMode = determineChatbotMode();
    console.log(`Tangerine Chatbot opened in ${currentMode} mode`);
  };

  const handleClose = () => {
    // Custom close logic
    console.log('Tangerine Chatbot closed');
  };

  // Create initial state with correct mode to prevent mode-change-triggered conversation reset
  const initialChatbotState = useMemo(() => {
    return {
      mode: determineChatbotMode(), // Set the correct initial mode
    };
  }, []); // Only run once on mount

  // Check if we're on an excluded route FIRST to prevent wrapper from being applied
  // This prevents component remounting when auth state changes on excluded routes (e.g., /logout)
  // KEEP this early return - these routes shouldn't have ANY chatbot infrastructure
  if (!shouldRenderOnCurrentRoute) {
    return children;
  }

  // Calculate UI visibility (separate from isAIEnabled state)
  // Hide the floating button when:
  // - AI is not enabled for this user
  // - Route is in the hidden floating button list
  // - User is not a student (teachers get the AI panel, not the floating tutor)
  const shouldHideChatbotUI = !isAiEnabled || shouldHideFloatingButton || !isStudent;

  // Provider always mounts (context available for hooks like useAssessmentResultIndividualQuestion)
  // UI only loads when AI is enabled (lazy loaded via ChatbotWidget)
  return (
    <ChatbotWidget
      isAIEnabled={isAiEnabled}
      initialState={initialChatbotState}
      emptyStateTitle={emptyStateTitle}
      emptyStateText={emptyStateText}
      excludeRoutes={excludeRoutes}
      chatService={tangerineChatService}
      className={`tangerine-ai-chatbot`}
      getChatContextData={getChatContextData}
      animationDuration={500}
      onOpen={handleOpen}
      onClose={handleClose}
      focusOnOpen={true}
      buttonIconName="chat"
      practicePageUrls={practicePageUrls}
      determineChatbotMode={determineChatbotMode}
      hideFloatingButton={shouldHideChatbotUI}
    >
      {children}
    </ChatbotWidget>
  );
}

TangerineAIChatbot.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TangerineAIChatbot;
